import { createContext, forwardRef, memo, useImperativeHandle, useMemo, useState } from "react"
import { formatHTML, getOnlyBreaksTexts, TITLE_BREAK_PREFIX } from "./utils"
import useCustomContext from "../../../hooks/useCustomContext"
import { getPropSafely } from "@repo/utils"
import { useIsDesktop } from "../../../providers/DeviceProvider"
import ClassicTextWithBreaks from "./Classic"
import StaticTextWithBreaks from "./Static"

// const ClassicTextWithBreaks = dynamic(
//     () =>
//         import('./Classic').then((mod) => {
//             const Comp = mod.default;
//             return ({customRef, ...props}) => <Comp {...props} ref={customRef}/>;
//         }),
//     {ssr: false}
// );
//
// const StaticTextWithBreaks = dynamic(
//     () =>
//         import('./Static').then((mod) => {
//             const Comp = mod.default;
//             return ({customRef, ...props}) => <Comp {...props} ref={customRef}/>;
//         }),
//     {ssr: false}
// );

export const VARIANT = Object.freeze({
	CLASSIC: "classic",
	STATIC: "static",
})

const TextWithBreaksContext = createContext({
	variant: VARIANT.CLASSIC,
	text: "",
	formatted: [],
	breaks: {},
	webGL: false,
	color: undefined,
	animation: "",
})

const TextWithBreaks = forwardRef(function TextWithBreaks(
	{
		data,
		className = "",
		variant: vt = VARIANT.CLASSIC,
		imageLoadPriority = false,
		color = "",
		webGL: gl = false,
		left = false,
		animation = undefined,
		disableInView = false,
		disableAnimation = false,
		onBreakClick = () => {},
		isStory = false,
		expensiveSpread = false,
		...props
	},
	fref,
) {
	const [wrapperRef, setWrapperRef] = useState(null)
	const text = getPropSafely(data, "text", "")
	const parentId = getPropSafely(data, "parentId", "", { silent: true })
	const breaks = getPropSafely(data, "breaks", [])
	const variant = useMemo(() => vt || VARIANT.CLASSIC, [vt])
	const webGL = variant === VARIANT.CLASSIC ? gl : false
	const isDesktop = useIsDesktop()
	const formatted = useMemo(() => {
		const src = formatHTML(text || "", TITLE_BREAK_PREFIX, expensiveSpread)
		return isDesktop ? src : [src.flat()]
	}, [text, isDesktop, expensiveSpread])
	const onlyBreaksTexts = useMemo(() => getOnlyBreaksTexts(formatted), [formatted])

	useImperativeHandle(
		fref,
		() => wrapperRef,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[wrapperRef, variant],
	)

	const context = useMemo(
		() => ({
			variant,
			text,
			formatted,
			breaks: { data: breaks, texts: onlyBreaksTexts },
			color,
			webGL,
			animation,
			onBreakClick,
			isStory,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[variant, text, formatted, breaks, onlyBreaksTexts, color, webGL, animation, isStory],
	)

	return (
		<TextWithBreaksContext.Provider value={context}>
			{variant === VARIANT.CLASSIC ? (
				<ClassicTextWithBreaks
					ref={setWrapperRef}
					className={className}
					imageLoadPriority={imageLoadPriority}
					parentId={parentId}
					left={left}
					{...props}
				/>
			) : (
				<StaticTextWithBreaks
					ref={setWrapperRef}
					className={className}
					imageLoadPriority={imageLoadPriority}
					parentId={parentId}
					left={left}
					disableInView={disableInView}
					disableAnimation={disableAnimation}
					{...props}
				/>
			)}
		</TextWithBreaksContext.Provider>
	)
})

export function useTextWithBreaksContext(selector) {
	return useCustomContext(TextWithBreaksContext, selector)
}

export default memo(TextWithBreaks)
